import type { Block, Section, SectionPlanItem } from "../contract"
import { walkBlocks } from "./format"

export interface Weighing {
  helps: number
  hurts: number
  /** Decisions against the reader that bind them. */
  bindingAgainst: number
}

/** Count the decisions blocks across a turn's sections. */
export function weigh(sections: Record<string, Section>): Weighing {
  const out: Weighing = { helps: 0, hurts: 0, bindingAgainst: 0 }
  for (const s of Object.values(sections)) {
    walkBlocks(s.blocks ?? [], (b) => {
      if (b.type !== "decisions") return
      const n = (b.items ?? []).length
      if (b.lean === "helps") out.helps += n
      else {
        out.hurts += n
        out.bindingAgainst += (b.items ?? []).filter((i) => i.binds).length
      }
    })
  }
  return out
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** "3 decisions help, 1 binds against you." Empty when no decisions were weighed. */
export function weighingLine(w: Weighing): string {
  const parts: string[] = []
  if (w.helps) parts.push(plural(w.helps, "decision helps", "decisions help"))
  if (w.hurts) {
    const other = w.hurts - w.bindingAgainst
    if (w.bindingAgainst) parts.push(`${plural(w.bindingAgainst, "binds", "bind")} against you`)
    if (other) parts.push(`${plural(other, "goes", "go")} against you`)
  }
  return parts.length ? `${parts.join(", ")}.` : ""
}

/** The first block of a kind in a section, looking inside disclosures. */
export function firstBlock<T extends Block["type"]>(section: Section | undefined, type: T): Extract<Block, { type: T }> | undefined {
  let found: Extract<Block, { type: T }> | undefined
  if (!section) return undefined
  walkBlocks(section.blocks ?? [], (b) => {
    if (!found && b.type === type) found = b as Extract<Block, { type: T }>
  })
  return found
}

/** Sections to read first, and the rest, in plan order. */
export function readingGroups(plan: SectionPlanItem[]): { now: number[]; then: number[] } {
  const now: number[] = []
  const then: number[] = []
  plan.forEach((p, i) => (p.read_now ? now : then).push(i))
  return now.length ? { now, then } : { now: [], then: plan.map((_, i) => i) }
}
